import { BrazeFullCard, NotificationCards, NotificationInnerStyle, PinnedBadge } from "./Notification.style";
import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";

import { AvatarCustom } from "../../../components";
import { BrazeContentCard } from "../../../services/braze/braze.types";
import { PinnedBadgeIcon } from "../../../components/Icons";
import { timeAgo } from "../../../utils/date";

type Props = {
  card: BrazeContentCard;
  logImpression: (card: BrazeContentCard) => void;
  logClick: (card: BrazeContentCard) => void;
};

const CTA_COLOR = "#E5AF0A";
const linkTextStyle = {
  "color": CTA_COLOR,
  "cursor": "pointer",
  "width": "fit-content",
  "&:hover": { opacity: 0.8 },
};

const PinnedLabel = () => (
  <PinnedBadge>
    <PinnedBadgeIcon width={12} height={12} />
    Pinned
  </PinnedBadge>
);

type CardTextBlockProps = {
  card: BrazeContentCard;
  onLinkClick: (e: React.MouseEvent) => void;
  titleMaxWidth?: string;
};

const CardTextBlock = ({ card, onLinkClick, titleMaxWidth }: CardTextBlockProps) => (
  <>
    {card.title &&
      <Typography
        variant="body1"
        sx={{ fontWeight: 600, maxWidth: titleMaxWidth, wordWrap: "break-word" }}
      >
        {card.title}
      </Typography>}
    {card.description &&
      <Typography variant="body1" sx={{ color: (theme) => theme.palette.gray.main }}>
        {card.description}
      </Typography>}
    {card.createdAt &&
      <Typography variant="body1" sx={{ fontSize: "12px", color: (theme) => theme.palette.gray.dark }}>
        {timeAgo(card.createdAt)}
      </Typography>}
    {card.linkText &&
      <Typography variant="body1" fontWeight="bold" onClick={onLinkClick} sx={linkTextStyle}>
        {card.linkText}
      </Typography>}
  </>
);

const BrazeNotificationCard = (props: Props) => {
  const { card, logImpression, logClick } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = rootRef.current;

    if (!node || typeof IntersectionObserver === "undefined") {
      logImpression(card);

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            logImpression(card);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [card, logImpression]);

  const handleClick = () => {
    logClick(card);
    if (card.url) {
      window.open(card.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClick();
  };

  const isFullCard = card.cardType === "captioned" || card.cardType === "imageOnly";

  if (isFullCard) {
    // With an image the badge floats over the image (no text below it), so the
    // title keeps its full width. Without an image the badge overlays the text
    // block, so shrink the first title line to leave room for the badge.
    return (
      <BrazeFullCard
        ref={rootRef}
        onClick={handleClick}
        sx={{ position: "relative", cursor: card.url ? "pointer" : "default" }}
      >
        {card.pinned && <PinnedLabel />}
        {card.imageUrl &&
          <Box
            component="img"
            src={card.imageUrl}
            alt={card.title || "content card"}
            sx={{ width: "100%", maxHeight: "180px", objectFit: "cover", display: "block" }}
          />}
        {(card.title || card.description || card.linkText) &&
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", padding: "12px 16px" }}>
            <CardTextBlock
              card={card}
              onLinkClick={handleLinkClick}
              titleMaxWidth={card.pinned && !card.imageUrl ? "calc(100% - 84px)" : undefined}
            />
          </Box>}
      </BrazeFullCard>
    );
  }

  // Classic card: avatar + text row. The badge overlays the top-right corner,
  // so cap the title width to leave room and avoid clipping it under the badge.
  return (
    <NotificationCards ref={rootRef} onClick={handleClick} sx={{ position: "relative" }}>
      {card.pinned && <PinnedLabel />}
      {card.imageUrl &&
        <Box>
          <AvatarCustom image={card.imageUrl} width={40} height={40} />
        </Box>}
      <Box sx={NotificationInnerStyle}>
        <CardTextBlock
          card={card}
          onLinkClick={handleLinkClick}
          titleMaxWidth={card.pinned ? "calc(90% - 84px)" : "90%"}
        />
      </Box>
    </NotificationCards>
  );
};

export default BrazeNotificationCard;
